import { ReactNode, useState } from 'react';

import { Button, Table } from '@iwoca/orion';

import { OngoingPaymentPlan } from '../../Dashboard.types';
import { formatCurrency, formatRate } from '../../utils/currency';
import { formatDate } from '../../utils/date';

type ScheduleEntry = {
  date: string;
  principal: number;
  interest: number;
  total: number;
};

const RepaymentsSchedule = ({
  schedule,
  totalInterest,
  totalPrinciple,
  totalPayable,
  overrideScheduleDates,
}: {
  schedule: ScheduleEntry[];
  totalInterest: number;
  totalPrinciple: number;
  totalPayable: number;
  overrideScheduleDates?: (date: string, index: number) => string;
}) => {
  const [showAll, setShowAll] = useState(false);

  const FormatRepaymentDate = ({
    repayment,
    index,
  }: {
    repayment: ScheduleEntry;
    index: number;
  }) => {
    if (overrideScheduleDates) {
      return overrideScheduleDates(repayment.date, index);
    }

    return formatDate(repayment.date);
  };

  return (
    <>
      <Table
        data-testid="repaymentScheduleTable"
        columns={[
          {
            id: 'date',
            header: 'Date',
            cell: ({ row }) => (
              <FormatRepaymentDate repayment={row.original} index={row.index} />
            ),
            footer: 'Total',
          },
          {
            id: 'principle',
            header: 'Outstanding amount',
            cell: ({ row }) => <>{formatCurrency(row.original.principal)}</>,
            footer: () => <>{formatCurrency(totalPrinciple)}</>,
          },
          {
            id: 'interest',
            header: 'Interest*',
            cell: ({ row }) => <>{formatCurrency(row.original.interest)}</>,
            footer: () => <>{formatCurrency(totalInterest)}</>,
          },
          {
            id: 'total',
            header: 'Total',
            cell: ({ row }) => <>{formatCurrency(row.original.total)}</>,
            footer: () => <>{formatCurrency(totalPayable)}</>,
          },
        ]}
        data={showAll ? schedule : schedule.slice(0, 3)}
      />
      {schedule.length > 3 && (
        <Button
          variant="tertiary"
          size="small"
          onClick={() => setShowAll(!showAll)}
          className="mt-2"
        >
          {showAll ? <span>Show less</span> : <span>View full schedule</span>}
        </Button>
      )}
    </>
  );
};

const OrderSummary = ({
  title,
  paymentPlan,
}: {
  title?: ReactNode;
  paymentPlan: OngoingPaymentPlan;
}) => {
  const { account, interestRate } = paymentPlan;
  const scheduledPayments = account.scheduled_payments;

  if (!scheduledPayments) return null;

  const schedule = scheduledPayments.schedule as ScheduleEntry[];
  const totalPayable = scheduledPayments.summary?.total_payable ?? 0;

  const totalPrinciple = schedule.reduce(
    (total, entry) => total + entry.principal,
    0,
  );
  const totalInterest = schedule.reduce(
    (total, entry) => total + entry.interest,
    0,
  );

  return (
    <div className="flex flex-col">
      <h2 className="m-0">{title || 'Your payment plan'}</h2>

      <h3 data-testid="order-amount-title" className="mb-5 text-[#184681]">
        (Today&apos;s outstanding amount:{' '}
        {formatCurrency(account.balance?.total ?? 0)})
      </h3>

      <RepaymentsSchedule
        schedule={schedule}
        totalInterest={totalInterest}
        totalPrinciple={totalPrinciple}
        totalPayable={totalPayable}
      />

      <p
        data-testid="interest-disclaimer"
        className="font-light mx-11 mb-0 text-center text-[#184681] sm:mx-0"
      >
        *Your interest rate is {formatRate(interestRate)} per month.
      </p>
    </div>
  );
};

export default OrderSummary;
